import createCachedSelector from 're-reselect'
import { diff } from 'util/date'
import {
  calculateProrataAmount,
  getPastTimestamp,
  getFutureTimestamp,
  buildLineItemsFromPricings,
  calculateSubscriptionFlatTotal,
  calculateSubscriptionUsageTotal,
} from '../util'
import { selectAppliedDiscount } from './selectAppliedDiscount'
import { selectNextBillDate } from './selectNextBillDate'
import { selectPricingsById } from './selectPricingsById'
import { selectUsage } from './usage/selectUsage'
import { BILLING_CYCLE_MONTHLY } from '../constants'

export const selectUpgradeBreakdownForPricingIds = createCachedSelector(
  (_state, currentPricingIds) => currentPricingIds,
  (_state, _currentPricingId, newPricingIds) => newPricingIds,
  (_state, _currentPricingId, _newPricingId, currentBillingCycle) =>
    currentBillingCycle,
  (_state, _currentPricingId, _newPricingId, _currentCycle, newBillingCycle) =>
    newBillingCycle,
  (
    _state,
    _currentPricingId,
    _newPricingId,
    _currentCycle,
    _newBillingCycle,
    upgradeAt
  ) => upgradeAt,
  (...args) => args[6],
  selectUsage,
  selectPricingsById,
  selectNextBillDate,
  selectAppliedDiscount,
  (
    currentPricingIds,
    newPricingIds,
    currentBillingCycle,
    newBillingCycle,
    upgradeAt,
    pricingQuantitiesById,
    usage,
    pricingsById,
    currentCycleEndAtSeconds,
    discount
  ) => {
    const currentCycleEndAt = currentCycleEndAtSeconds * 1000

    const currentPricings = currentPricingIds.map(id => pricingsById[id])
    const newPricings = newPricingIds.map(id => pricingsById[id])

    const currentCycleStart = getPastTimestamp(
      currentCycleEndAt,
      currentBillingCycle
    )
    const nextCycleEnd = getFutureTimestamp(currentCycleStart, newBillingCycle)

    const monthlyRate = (pricings, billingCycle) => {
      const lineItems = buildLineItemsFromPricings({
        pricings,
        billingCycle,
        usage,
        discount,
        pricingQuantitiesById,
      })
      const cycleTotal =
        calculateSubscriptionFlatTotal(lineItems) +
        calculateSubscriptionUsageTotal(lineItems)
      return (
        cycleTotal / 100 / (billingCycle === BILLING_CYCLE_MONTHLY ? 1 : 12)
      )
    }

    const prorataRefund = calculateProrataAmount(
      monthlyRate(currentPricings, currentBillingCycle),
      upgradeAt,
      currentCycleEndAt
    )

    const refundDays = diff('days', upgradeAt, currentCycleEndAt)

    const prorataCharge = calculateProrataAmount(
      monthlyRate(newPricings, newBillingCycle),
      upgradeAt,
      nextCycleEnd
    )

    const chargeDays = diff('days', upgradeAt, nextCycleEnd)

    return {
      currentPricingIds,
      newPricingIds,
      refundDays,
      prorataRefund: Math.round(prorataRefund * 100),
      prorataCharge: Math.round(prorataCharge * 100),
      chargeDays,
    }
  }
)(
  (
    _state,
    currentPricingIds,
    newPricingIds,
    currentBillingCycle,
    newBillingCycle,
    upgradeAt
  ) =>
    [
      currentPricingIds.join('|'),
      newPricingIds.join('|'),
      currentBillingCycle,
      newBillingCycle,
      upgradeAt,
    ].join('-')
)
